import moment from "moment";

// 주문 방식
export const orderType = {
  PICKUP: "픽업",
  DELIVERY: "배달",
  SHOP: "매장",
};

// 주문 상태
export const orderStatus = {
  PENDING: "주문대기",
  CONFIRMED: "주문승인",
  READY: "상품준비",
  CANCELLED: "주문취소",
  CANCEL_REQUESTED: "주문취소요청",
  COMPLETED: "상품준비완료",
  PICKUP: "주문완료",
};

// 주문 ID 마지막 6자리만 반환
export const formatOrderId = (id) => {
  if (!id) return "";
  return id.toString().slice(8, id.length);
};

// 현재부터 수령까지 남은 분 계산
export const calculateTime = (item) => {
  const { pickupBy } = item;
  if (!pickupBy) return null;

  const now = moment();
  const target = moment(pickupBy);

  const duration = target.diff(now, "minutes");
  return duration;
};

// 남은 시간에 따라 상태 색상
export const calculateStatus = (order) => {
  const { createdAt, pickupBy } = order;
  if (!createdAt || !pickupBy) return "gray";

  const start = moment(createdAt);
  const end = moment(pickupBy);
  const now = moment();

  const total = end.diff(start, "minutes");
  const remaining = end.diff(now, "minutes");

  if (remaining < 0 || total <= 0) return "red";

  const ratio = remaining / total;

  if (ratio > 2 / 3) return "green";
  if (ratio > 1 / 3) return "yellow";
  return "red"; // 0 ~ 1/3
};

// 진행률에 따른 정수 반환
export const getWaveTop = (order) => {
  const minTop = 7; // (100%)
  const maxTop = 65; // (0%)

  const { createdAt, pickupBy } = order;
  if (!createdAt || !pickupBy) return maxTop;

  const start = moment(createdAt);
  const end = moment(pickupBy);
  const now = moment();

  const total = end.diff(start, "minutes");
  const elapsed = now.diff(start, "minutes");

  if (total <= 0) return minTop;
  if (elapsed <= 0) return maxTop;

  const ratio = elapsed / total;
  const progress = Math.max(Math.min(ratio, 1), 0);

  return maxTop - (maxTop - minTop) * progress;
};

// 주문 리스트를 상태별로 분류
export const categorizeItemsByStatus = (orders = []) => {
  const categorized = {
    PENDING: [],
    CONFIRMED: [],
    READY: [],
    CANCELLED: [],
    COMPLETED: [],
    PICKUP: [],
  };

  orders.forEach((item) => {
    switch (item.status) {
      case "PENDING":
      case "CANCEL_REQUESTED":
        categorized.PENDING.push(item);
        break;
      case "CONFIRMED":
      case "READY":
      case "COMPLETED":
        categorized.READY.push(item);
        break;
      case "CANCELLED":
      case "PICKUP":
        categorized.PICKUP.push(item);
        break;
      default:
        break;
    }
  });

  return categorized;
};

export const formatQuantity = (quantity) => {
  return String(quantity) === `-1`
    ? "무제한"
    : `${formatNumber(quantity)}개${quantity < 10 ? ` (10개 미만)` : ""}`;
};

// 주문 상태에 따른 이미지 반환
export const renderStatusImage = (status) => {
  switch (status) {
    case "PENDING":
      return null;
    case "CONFIRMED":
    case "READY":
    case "COMPLETED":
      return <img src="/img/store/status_ready.png" />;
    case "CANCELLED":
      return <img src="/img/store/status_cancelled.png" />;
    case "PICKUP":
      return <img src="/img/store/status_pickup.png" />;
    default:
      return null;
  }
};

// 상품 초기값
export const defaultProduct = {
  categoryId: null,
  description: "",
  media: [],
  name: "",
  price: "",
  quantity: -1,
  specifications: "",
  useDisplay: true,
  status: "READY",
  tags: [],
  tag: "",
  brand: "",
  shortDescription: "",
};

export const defaultOpt = {
  addPrice: "",
  quantity: "",
  value: "",
  consumeCount: 1,
  status: "READY",
};

// 옵션 초기값
export const defaultOption = {
  cmpnyNo: null,
  isRequired: true,
  name: "",
  codeName: "",
  options: [defaultOpt],
};

export const tooltips = {
  codeName: [
    {
      title: "관리옵션그룹명",
      description: "관리자 전용 이름입니다. 고객에게는 보이지 않습니다.",
      example: "예) 옵션1",
    },
  ],
  types: [
    {
      title: "필수 옵션",
      description: "고객이 주문 시 반드시 선택해야 하는 옵션",
      example: "예) 맵기 단계",
    },
    {
      title: "선택 옵션",
      description: "고객이 필요에 따라 추가할 수 있는 옵션",
      example: "예) 추가 토핑, 사이즈업",
    },
  ],
  options: [
    {
      title: "옵션상태",
      description: "옵션의 현재 판매 상태를 나타냅니다.\n",
      example:
        "판매중: 홈페이지에 노출되며 구매 가능\n" +
        "품절: 홈페이지에 노출되지만 구매 불가\n" +
        "일시중지: 홈페이지에 노출되지 않으며 관리자만 확인 가능\n",
    },
    {
      title: "옵션재고",
      description: "현재 보유 중인 해당 옵션의 재고 수량",
    },
    {
      title: "옵션수량",
      description: "주문 1건당 소모되는 옵션 수량",
    },
  ],
};

export const toMenuTooltip = () =>
  tooltips.options.map((item) =>
    item.example ? { ...item, example: item.example.replace(/홈페이지에/g, "메뉴판에") } : item,
  );

// 상품 상태 공통 정의
export const status = {
  READY: "판매중",
  SOLDOUT: "품절",
  PAUSE: "일시중지",
};

const statusTooltipMap = {
  READY: "노출되고 구매가 가능합니다.",
  SOLDOUT: "노출되고 구매가 불가능합니다.",
  PAUSE: "노출되지 않고 관리자만 확인할 수 있습니다.",
};

export const getStatusTooltip = (label = "메뉴판") =>
  Object.entries(status).map(([key, title]) => ({
    title,
    description: `${label}에 ${statusTooltipMap[key]}`,
  }));

// 메뉴 그룹 초기값
export const defaultMenuGroup = (menuPlatform = "PICKUP") => {
  return {
    name: "",
    sortOrder: 0,
    menuPlatform,
    products: [],
  };
};

export const menuPlatform = {
  PICKUP: "상생매창",
  ECHANGGO: "e층창고",
  ALL: "전체",
};

// 회사 정보 초기값
export const defaultCompany = {
  cmpnyImage: [],
  cmpnyNm: "",
  cmpnySe: "",
  cmpnyBadge: [],
  cmpnyBadgeText: [],
  addr: "",
  addrDetail: "",
  tel: "",
};

// 요일 이름 매핑
export const weekdays = {
  0: "일요일",
  1: "월요일",
  2: "화요일",
  3: "수요일",
  4: "목요일",
  5: "금요일",
  6: "토요일",
};

// 매장 운영시간 기본값
export const defaultStoreTime = {
  openTime: "09:00",
  closeTime: "18:00",
  breakStartTime: "",
  breakEndTime: "",
  isOpen: true,
  showBreak: false,
};

// 운영시간 전체 기본값
export const defaultTime = {
  openSetting: {
    hasHoliday: false,
    hourType: "same",
  },

  openTimeData: {
    same: { ...defaultStoreTime },
    weekday: { ...defaultStoreTime },
    weekend: { ...defaultStoreTime },
    byDay: Array.from({ length: 7 }, (_, i) => ({
      weekday: i,
      ...defaultStoreTime,
    })),
  },
};

// 데이터 변환
export const convertBusinessHours = (hours) => {
  const byDay = hours.map((item) => ({
    weekday: item.weekday,
    isOpen: item.isOpen,
    openTime: item.openTime || "",
    closeTime: item.closeTime || "",
    breakStartTime: item.breakStartTime || "",
    breakEndTime: item.breakEndTime || "",
    showBreak: !!(item.breakStartTime && item.breakEndTime),
  }));

  const clean = ({ openTime, closeTime, breakStartTime, breakEndTime }) => ({
    openTime,
    closeTime,
    breakStartTime,
    breakEndTime,
  });

  const weekdayItems = byDay.filter((d) => d.weekday >= 1 && d.weekday <= 5);
  const weekendItems = byDay.filter((d) => d.weekday === 0 || d.weekday === 6);

  const isEqualTime = (a, b) => JSON.stringify(clean(a)) === JSON.stringify(clean(b));

  const checkSame = (list) => {
    const opened = list.filter((d) => d.isOpen);
    if (opened.length === 0) return false;
    const base = opened[0];
    return opened.every((d) => isEqualTime(d, base));
  };

  const allSame = checkSame(byDay);
  const weekdaySame = checkSame(weekdayItems);
  const weekendSame = checkSame(weekendItems);

  const hourType = allSame ? "same" : weekdaySame && weekendSame ? "weekday-weekend" : "by-day";

  const hasHoliday = byDay.some((d) => d.isOpen === false);

  const findFirstOpen = (list) => list.find((d) => d.isOpen) || defaultStoreTime;

  return {
    openSetting: {
      hasHoliday,
      hourType,
    },
    openTimeData: {
      same: allSame ? { ...findFirstOpen(byDay) } : { ...defaultStoreTime },
      weekday: weekdaySame ? { ...findFirstOpen(weekdayItems) } : { ...defaultStoreTime },
      weekend: weekendSame ? { ...findFirstOpen(weekendItems) } : { ...defaultStoreTime },
      byDay,
    },
  };
};

export const is24Hours = (item) => {
  return item.openTime === "00:00" && item.closeTime === "00:00";
};

export const getBizTimeSummary = (timeData) => {
  if (!Array.isArray(timeData) || !timeData.length) return [];

  const normalize = (v) => (v == null ? "" : v);

  const isSameDay = (a, b) =>
    a.isOpen === b.isOpen &&
    normalize(a.openTime) === normalize(b.openTime) &&
    normalize(a.closeTime) === normalize(b.closeTime) &&
    normalize(a.breakStartTime) === normalize(b.breakStartTime) &&
    normalize(a.breakEndTime) === normalize(b.breakEndTime);

  const is24Hours = (d) => d.isOpen && d.openTime === "00:00" && d.closeTime === "00:00";

  const getTimeText = (d) => {
    if (!d.isOpen) return "휴무";
    if (is24Hours(d)) return "24시간 운영";
    const range = `${d.openTime} ~ ${d.closeTime}`;
    const brk =
      d.breakStartTime && d.breakEndTime
        ? ` (브레이크타임: ${d.breakStartTime} ~ ${d.breakEndTime})`
        : "";
    return range + brk;
  };

  const result = [];

  const allSame = timeData.every((d) => isSameDay(d, timeData[0]));

  if (allSame) {
    result.push(`매일 ${getTimeText(timeData[0])}`);
    return result;
  }

  const weekdaysList = timeData.filter((d) => d.weekday >= 1 && d.weekday <= 5);
  const weekendsList = timeData.filter((d) => d.weekday === 0 || d.weekday === 6);

  const weekdaySame = weekdaysList.every((d) => isSameDay(d, weekdaysList[0]));
  const weekendSame = weekendsList.every((d) => isSameDay(d, weekendsList[0]));

  // if (weekdaySame && weekendSame) {
  //   result.push(`평일 ${getTimeText(weekdaysList[0])}`);
  //   result.push(`주말 ${getTimeText(weekendsList[0])}`);
  //   return result;
  // }

  // if (weekdaySame) {
  //   result.push(`평일 ${getTimeText(weekdaysList[0])}`);
  //   weekendsList.forEach((d) => {
  //     result.push(`${weekdays[d.weekday]} ${getTimeText(d)}`);
  //   });
  //   return result;
  // }

  // if (weekendSame) {
  //   weekdaysList.forEach((d) => {
  //     result.push(`${weekdays[d.weekday]} ${getTimeText(d)}`);
  //   });
  //   result.push(`주말 ${getTimeText(weekendsList[0])}`);
  //   return result;
  // }

  // 모두 다를 경우
  timeData.forEach((d) => {
    result.push(`${weekdays[d.weekday].replace("요일", "")} ${getTimeText(d)}`);
  });

  return result;
};

// 저장용 데이터 변환 함수
export const buildSubmitData = (data, type) => {
  const is24Hours = (item) => item.openTime === "00:00" && item.closeTime === "00:00";

  const clean = (base, isOpen, weekday) => ({
    weekday,
    isOpen,
    openTime: isOpen ? base.openTime : "00:00",
    closeTime: isOpen ? base.closeTime : "00:00",
    breakStartTime:
      isOpen && base.showBreak && !is24Hours(base) && base.breakStartTime
        ? base.breakStartTime
        : null,
    breakEndTime:
      isOpen && base.showBreak && !is24Hours(base) && base.breakEndTime ? base.breakEndTime : null,
  });

  const getIsOpen = (weekday) => data.byDay.find((d) => d.weekday === weekday)?.isOpen ?? true;

  return Array.from({ length: 7 }, (_, weekday) => {
    let base;

    switch (type) {
      case "same":
        base = data.same;
        break;
      case "weekday-weekend":
        base = weekday === 0 || weekday === 6 ? data.weekend : data.weekday;
        break;
      case "by-day":
        base = data.byDay.find((d) => d.weekday === weekday) || defaultStoreTime;
        break;
      default:
        base = defaultStoreTime;
    }

    const isOpen = getIsOpen(weekday);
    return clean(base, isOpen, weekday);
  });
};

export const productQuantityState = (item) => {
  if (item.quantity === -1) return null;
  if (!item.quantity || item.status === "SOLDOUT") {
    return { status: "soldout", message: "품절" };
  }
  if (item.quantity < 10) {
    return { status: "low", message: `재고 ${formatNumber(item.quantity)}개` };
  }
  if (item.quantity > 10) {
    return { status: "available", message: `재고 ${formatNumber(item.quantity)}개` };
  }
  return null;
};

export const productDisplayState = (item) => {
  return item?.useDisplay ? "공개" : "비공개";
};
